/**
 * AppHostContext — the host app (any `frontend/apps/<name>/`) supplies its
 * concrete configs, registries, and domain atoms via this single context.
 * Ui code reads from it via `useAppHost()` instead of reaching into the
 * app's tree via `@/...` (which would couple ui to one specific app's
 * directory structure and prevent a second app from cleanly consuming ui).
 *
 * Atoms surfaced here use **structured types** rather than `any` — that
 * way ui's expectations are an explicit contract that any host can target,
 * not a duck-typed leak of the current app's domain model. The host
 * derives these atoms from its own concrete domain atoms (PDM Product,
 * task lookups, workspace fetch, etc.) and supplies them shaped to fit.
 */

'use client';

import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import type { Atom } from 'jotai';

export interface MiniAppRegistryEntry {
  Component: ComponentType<{ containerWidth: number }>;
}

export interface SidebarItem {
  icon: string;
  label: string;
}

/**
 * The "current navigation context entity" — whatever the host considers
 * the focus of the current route (a PDM Product, a Document, a Project,
 * etc.). Ui only needs id + name; the host can derive this from its own
 * richer typed atom.
 */
export interface NavigationContextEntity {
  id: string;
  name: string;
}

/**
 * Minimal shape ui needs to "open existing chat" for the navigation entity.
 */
export interface NavigationContextTask {
  id: string;
}

/** Options accepted by the host's chat-task factory. */
export interface CreateChatTaskOptions {
  /**
   * Task subclass name (e.g. "ChatTask", "ChatSkillsTask", "RFLPTask"). The
   * host looks this up in ModelRegistry and constructs that class; defaults
   * to "ChatTask".
   */
  taskClassName?: string;
}

export interface AppHostContextValue {
  /** Whether routes show the shell (tabs/sidebar/chat) by default. */
  shellDefault: boolean;

  /** Returns the extra column field names to render on list views for a model. */
  getListColumns: (modelName: string) => readonly string[] | undefined;

  /** Mini-app components keyed by mini-app icon/name. The shell switches on this. */
  miniAppRegistry: Record<string, ComponentType<{ containerWidth: number }>>;

  /** Sidebar entries (icon + label). */
  sidebarItems: readonly SidebarItem[];

  /** Component the chat ToolCallPart renderers use to render spawned sub-tasks. */
  SpawnedTaskComponent: ComponentType<any>;

  /**
   * The current navigation context entity (the host's "current product"-like
   * thing). Ui treats this as a `{ id, name }` pair — that's the contract;
   * the host's own atom may carry a richer typed value internally.
   */
  currentEntityAtom: Atom<NavigationContextEntity | null>;

  /**
   * The chat-style Task currently attached to the current entity, if any.
   * The host derives this from its domain (e.g. by reading
   * `currentProduct.current_tasks` and picking the most recent). Ui only
   * needs an `id` to navigate to.
   */
  currentEntityTaskAtom: Atom<NavigationContextTask | null>;

  /**
   * Workspace title shown in the mini-app header. Host-derived from its own
   * workspace-shaped state. Null while the workspace is still loading.
   */
  workspaceNameAtom: Atom<string | null>;

  /**
   * Host-supplied factory that creates a new chat-style task and attaches
   * it to the current entity. Ui's "New Chat" buttons call this; all
   * domain-coupled steps (`Task.save()`, `currently_attached_to.set(...)`,
   * Re-saving via `makeEditable()`) live in the host. Returns the new
   * task's id if it succeeded, or null on failure / when there's no
   * current entity.
   */
  createNewChatTaskForCurrentEntity: (
    options?: CreateChatTaskOptions,
  ) => Promise<NavigationContextTask | null>;

  /**
   * Host-supplied: a new chat that works on `projectId`, for a chat that
   * was refused a move to that project (a chat stays on its project; Tom,
   * 2026-10-20). Answers the chat's id and the page to open it on, or null
   * on failure. Omitted by a host with no projects: the offer is not shown.
   */
  createChatForProject?: (projectId: string, projectName?: string | null) => Promise<{ id: string; href: string } | null>;
}

const AppHostContext = createContext<AppHostContextValue | null>(null);

export function AppHostProvider({
  value,
  children,
}: {
  value: AppHostContextValue;
  children: ReactNode;
}) {
  return <AppHostContext.Provider value={value}>{children}</AppHostContext.Provider>;
}

export function useAppHost(): AppHostContextValue {
  const ctx = useContext(AppHostContext);
  if (!ctx) {
    throw new Error('useAppHost() must be called inside <AppHostProvider>. Wrap your app root.');
  }
  return ctx;
}
